import type { WorkCenterDto } from "@grizzly/shared";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useI18n } from "../../i18n/index.js";
import { Plus, Trash2 } from "lucide-react";

export interface OperationDraft {
  workCenterId: string;
  description: string;
  setupMinutes: string;
  runMinutesPerUnit: string;
}

export function OperationsEditor({
  operations,
  workCenters,
  onChange,
}: {
  operations: OperationDraft[];
  workCenters: WorkCenterDto[];
  onChange(operations: OperationDraft[]): void;
}) {
  const { t } = useI18n();
  const add = () =>
    onChange([
      ...operations,
      { workCenterId: "", description: "", setupMinutes: "0", runMinutesPerUnit: "0" },
    ]);

  const patch = (idx: number, next: OperationDraft) =>
    onChange(operations.map((s, i) => (i === idx ? next : s)));

  const remove = (idx: number) => onChange(operations.filter((_, i) => i !== idx));

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">{t("jobs.operations.title")}</h3>
        <Button size="sm" variant="ghost" onClick={add}>
          <Plus className="h-4 w-4" aria-hidden="true" /> {t("jobs.operations.add")}
        </Button>
      </div>
      {operations.map((op, idx) => (
        <div key={idx} className="mb-2 rounded border border-line p-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <SelectInput
              label={t("common.fields.workCenter")}
              options={[
                { value: "", label: t("common.none") },
                ...workCenters.map((w) => ({ value: w.id, label: w.name })),
              ]}
              value={op.workCenterId}
              onChange={(e) => patch(idx, { ...op, workCenterId: e.target.value })}
            />
            <TextInput
              label={t("jobs.operations.operation")}
              value={op.description}
              onChange={(e) => patch(idx, { ...op, description: e.target.value })}
            />
            <TextInput
              label={t("common.fields.setupMinutes")}
              value={op.setupMinutes}
              onChange={(e) => patch(idx, { ...op, setupMinutes: e.target.value })}
            />
            <TextInput
              label={t("common.fields.runMinutes")}
              value={op.runMinutesPerUnit}
              onChange={(e) => patch(idx, { ...op, runMinutesPerUnit: e.target.value })}
            />
          </div>
          <Button size="sm" variant="ghost" onClick={() => remove(idx)}>
            <Trash2 className="h-4 w-4" aria-hidden="true" /> {t("common.remove")}
          </Button>
        </div>
      ))}
    </section>
  );
}
